import { useEffect, useState } from 'react'
import { getMyAccess } from '@/api/team'
import { useProjectStore } from '@/store/projectStore'

export const ProjectPermissions = {
  READ: 'project.read',
  DELETE_PROJECT: 'project.delete',
  MANAGE_TEAM: 'project.manage_team',
  UPDATE_PROPERTIES: 'project.update_properties',
  UPDATE_NAVIGATION: 'project.update_navigation',
  UPDATE_SPECIFICATIONS: 'project.update_specifications',
  EDIT_OBJECTS: 'project.edit_objects',
  LIVE_PREVIEW: 'project.live_preview',
  TEST: 'project.test',
  DEPLOY: 'project.deploy',
} as const

export type ProjectPermission = (typeof ProjectPermissions)[keyof typeof ProjectPermissions]

/**
 * Roles that grant ONLY the ability to run/use the application — never to open
 * the builder (designers, source editor, deploy). A user whose every project
 * role is one of these is "run-only": opening their project launches the Run
 * view, not the workbench. Mirrors the backend's builder-vs-runtime split in
 * `auth.py` (register-via-invitation redirect).
 */
export const RUN_ONLY_ROLES = ['RUNTIME_USER', 'APPLICATION_USER']

/** True when this access summary can run the app but cannot open the design. */
export function isRunOnlyAccess(roles: string[] | undefined, isOwner: boolean): boolean {
  if (isOwner) return false
  if (!roles || roles.length === 0) return false
  return roles.every((r) => RUN_ONLY_ROLES.includes(r))
}

/**
 * Returns helpers to check the current user's permissions on the currently
 * selected project. While the access info is still loading, helpers return
 * `true` (optimistic) for non-destructive checks and `false` for destructive
 * ones, so the UI doesn't flicker its buttons on first paint.
 *
 * Backend enforcement remains the source of truth — these helpers are for
 * UX (hiding/disabling buttons that would 403).
 */
export function useProjectPermissions() {
  const access = useProjectStore((s) => s.projectAccess)

  const loaded = access !== null
  const has = (p: ProjectPermission): boolean => {
    if (!access) return false
    return access.is_owner || access.permissions.includes(p)
  }

  return {
    loaded,
    isOwner: access?.is_owner ?? false,
    roles: access?.roles ?? [],
    permissions: access?.permissions ?? [],
    has,
    // Run-only: can use the app but not the builder. Only meaningful once
    // access has loaded (false while loading, so we don't misroute on paint).
    isRunOnly: loaded && isRunOnlyAccess(access?.roles, access?.is_owner ?? false),
    // Convenience flags for the most common checks.
    canRead: has(ProjectPermissions.READ) || (access?.is_owner ?? false),
    canEditObjects: has(ProjectPermissions.EDIT_OBJECTS),
    // Optimistic edit flag: while access is still loading, assume editable so a
    // designer's Save button doesn't flash to "View only" on first paint. Once
    // loaded, reflects the real EDIT_OBJECTS grant. Use this to gate Save UI.
    canEditObjectsOrLoading: !loaded || has(ProjectPermissions.EDIT_OBJECTS),
    canUpdateSpecs: has(ProjectPermissions.UPDATE_SPECIFICATIONS),
    canUpdateProperties: has(ProjectPermissions.UPDATE_PROPERTIES),
    canUpdateNavigation: has(ProjectPermissions.UPDATE_NAVIGATION),
    canLivePreview: has(ProjectPermissions.LIVE_PREVIEW),
    canTest: has(ProjectPermissions.TEST),
    canDeploy: has(ProjectPermissions.DEPLOY),
    canManageTeam: has(ProjectPermissions.MANAGE_TEAM),
    canDelete: access?.is_owner ?? false,
  }
}

// One shared answer per project. The Run view mounts a pane per screen and the
// panes come and go as the user navigates, so without this every screen change
// would re-ask the same question.
const LIVE_PREVIEW_CACHE = new Map<string, boolean>()
const LIVE_PREVIEW_INFLIGHT = new Map<string, Promise<boolean>>()

/**
 * Whether the current user holds `project.live_preview` on `project` — the
 * BUILDER's permission, and the one every author-only maintenance endpoint
 * requires (deploy, reset preview data, the preview log, redeploy-function).
 *
 * `useProjectPermissions` answers from the project store, which only the
 * WORKBENCH fills: the Run view (`/run/:project`) never calls `selectProject`,
 * so there a store-only check answers "no permissions" for everyone, author
 * included. This asks the access endpoint itself when the store has no answer
 * for this project — `/projects/{id}/team/me` enforces no permission beyond
 * membership, so a cross-tenant runtime guest can ask about themselves.
 *
 * `null` means "not known yet". Callers hiding an author-only affordance should
 * treat null as "don't show it yet" — appearing a moment late is better than
 * offering a runtime member a button whose only possible answer is 403.
 */
export function useCanLivePreview(project: string): boolean | null {
  const access = useProjectStore((s) => s.projectAccess)
  const fromStore =
    access && access.project_id === project
      ? access.is_owner || access.permissions.includes(ProjectPermissions.LIVE_PREVIEW)
      : null

  const [fetched, setFetched] = useState<boolean | null>(
    () => (project ? LIVE_PREVIEW_CACHE.get(project) ?? null : null),
  )

  useEffect(() => {
    if (!project || fromStore !== null) return
    const cached = LIVE_PREVIEW_CACHE.get(project)
    if (cached !== undefined) { setFetched(cached); return }
    let cancelled = false
    let inflight = LIVE_PREVIEW_INFLIGHT.get(project)
    if (!inflight) {
      inflight = getMyAccess(project)
        .then((res) => Boolean(
          res.is_owner || (res.permissions || []).includes(ProjectPermissions.LIVE_PREVIEW),
        ))
        // A failed lookup is not a licence: treat it as "not an author", which
        // only hides a tool, where the other way round shows a button that 403s.
        .catch(() => false)
        .then((ok) => { LIVE_PREVIEW_CACHE.set(project, ok); return ok })
        .finally(() => { LIVE_PREVIEW_INFLIGHT.delete(project) })
      LIVE_PREVIEW_INFLIGHT.set(project, inflight)
    }
    void inflight.then((ok) => { if (!cancelled) setFetched(ok) })
    return () => { cancelled = true }
  }, [project, fromStore])

  return fromStore ?? fetched
}

// One answer per project: may the caller BUILD it (project.edit_objects)?
const EDIT_OBJECTS_CACHE = new Map<string, Promise<boolean>>()

/**
 * Whether the current user holds `project.edit_objects` on `project` — owner,
 * Project Manager, Developer and Analyst. Asked of the access endpoint (the
 * Run view and drawers never fill the project store); cached per project.
 * A failed lookup answers false: it only hides a builder tool.
 */
export function canEditObjectsFor(project: string): Promise<boolean> {
  if (!project) return Promise.resolve(false)
  const store = useProjectStore.getState().projectAccess
  if (store && store.project_id === project) {
    return Promise.resolve(store.is_owner || store.permissions.includes(ProjectPermissions.EDIT_OBJECTS))
  }
  let p = EDIT_OBJECTS_CACHE.get(project)
  if (!p) {
    p = getMyAccess(project)
      .then((res) => Boolean(res.is_owner || (res.permissions || []).includes(ProjectPermissions.EDIT_OBJECTS)))
      .catch(() => { EDIT_OBJECTS_CACHE.delete(project); return false })
    EDIT_OBJECTS_CACHE.set(project, p)
  }
  return p
}
